import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../notifications/hooks/useNotification';
import { ApiError } from '../../../lib/http/ApiError';
import type { InstanceApiFactory } from '../api/instanceApi';
import { REGISTRATION_KEY_STATUS } from '../instanceTypes';
import type { GeneratedKey, InstanceRow, RegistrationKey } from '../instanceTypes';

type InstanceApi = ReturnType<typeof InstanceApiFactory>;

interface UseInstanceKeysOptions {
  instance: InstanceRow | null;
  open: boolean;
  api: InstanceApi;
  onRevoked?: () => void;
  /** Called after a key is successfully generated (so the list key-status column can refresh). */
  onGenerated?: () => void;
}

export interface UseInstanceKeysResult {
  keys: RegistrationKey[];
  keysLoading: boolean;
  generating: boolean;
  newKey: GeneratedKey | null;
  activeKeyCount: number;
  revokedKeyCount: number;
  hasActiveKey: boolean;
  revoking: boolean;
  handleGenerate: () => Promise<void>;
  handleRevoke: (key: RegistrationKey) => void;
  dismissNewKey: () => void;
  cancelConfirmOpen: boolean;
  keyToCancel: RegistrationKey | null;
  handleConfirmCancel: () => Promise<void>;
  handleDismissConfirm: () => void;
}

export function useInstanceKeys({ instance, open, api, onRevoked, onGenerated }: UseInstanceKeysOptions): UseInstanceKeysResult {
  const { t } = useTranslation('instances');
  const { notifyError, notifySuccess } = useNotification();

  const [keys, setKeys] = useState<RegistrationKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState<GeneratedKey | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [keyToCancel, setKeyToCancel] = useState<RegistrationKey | null>(null);

  const fetchKeys = useCallback(async () => {
    if (!instance) return;
    setKeysLoading(true);
    try {
      const data = await api.listKeys(instance.id);
      setKeys(data);
    } catch (err) {
      notifyError(err);
    } finally {
      setKeysLoading(false);
    }
  }, [instance, api, notifyError]);

  useEffect(() => {
    if (open && instance) {
      void fetchKeys();
    } else {
      setKeys([]);
      setNewKey(null);
      setCancelConfirmOpen(false);
      setKeyToCancel(null);
    }
  }, [open, instance, fetchKeys]);

  const handleGenerate = useCallback(async () => {
    if (!instance) return;
    setGenerating(true);
    setNewKey(null);
    try {
      const generated = await api.generateKey(instance.id);
      setNewKey(generated);
      await fetchKeys();
      onGenerated?.();
    } catch (err) {
      notifyError(err);
    } finally {
      setGenerating(false);
    }
  }, [instance, api, notifyError, fetchKeys, onGenerated]);

  const handleRevoke = useCallback((key: RegistrationKey) => {
    setKeyToCancel(key);
    setCancelConfirmOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!instance || !keyToCancel) return;
    setRevoking(true);
    try {
      await api.revokeKey(instance.id, keyToCancel.id);
      notifySuccess(t('revokeKey.success'));
      setCancelConfirmOpen(false);
      setKeyToCancel(null);
      await fetchKeys();
      onRevoked?.();
    } catch (err) {
      if (ApiError.isApiError(err) && err.error === 'REGISTRATION_KEY_ALREADY_CONSUMED') {
        notifyError(new Error(t('revokeKey.alreadyConsumedError')));
        setCancelConfirmOpen(false);
        setKeyToCancel(null);
        await fetchKeys();
      } else {
        notifyError(err);
      }
    } finally {
      setRevoking(false);
    }
  }, [instance, keyToCancel, api, notifySuccess, notifyError, t, fetchKeys, onRevoked]);

  const dismissNewKey = useCallback(() => setNewKey(null), []);

  const handleDismissConfirm = useCallback(() => {
    setCancelConfirmOpen(false);
    setKeyToCancel(null);
  }, []);

  const activeKeyCount = keys.filter(k => k.status === REGISTRATION_KEY_STATUS.ACTIVE).length;
  const revokedKeyCount = keys.filter(k => k.status === REGISTRATION_KEY_STATUS.REVOKED).length;
  const hasActiveKey = activeKeyCount > 0;

  return {
    keys,
    keysLoading,
    generating,
    newKey,
    activeKeyCount,
    revokedKeyCount,
    hasActiveKey,
    revoking,
    handleGenerate,
    handleRevoke,
    dismissNewKey,
    cancelConfirmOpen,
    keyToCancel,
    handleConfirmCancel,
    handleDismissConfirm,
  };
}
